import Button from "components/button";
import VendorAddressAccordion from "../add/components/vendor-address-accordion";
import VendorBranchAccordion from "../add/components/vendor-branch-accordion";
import VendorInformationAccordion from "../add/components/vendor-information-accordion";
import ConfirmationModal from "components/shared/confirmation-modal";
import useEditVendorDraft from "hooks/vendor/useEditVendorDraft";
import { useParams } from "react-router-dom";
import { useState, useCallback } from "react";
import { toast } from "react-toastify";

const EditVendorPage = () => {
  const { id } = useParams();
  const {
    t,
    openSections,
    toggleSection,
    vendorInfoForm,
    vendorBranchForm,
    vendorAddressForm,
    branchItems,
    addressItems,
    branchOptions,
    hasBillingAddress,
    editingBranchIndex,
    editingAddressIndex,
    isSavingVendor,
    isFetchingVendor,
    showSaveDraft,
    handleNextFromInformation,
    handleNextFromBranch,
    handleAddBranch,
    handleEditBranch,
    handleDeleteBranch,
    handleAddAddress,
    handleEditAddress,
    handleDeleteAddress,
    handleConfirmAddress,
    handleSaveDraft,
    handleSave,
    showCancelModal,
    setShowCancelModal,
    navigate,
    openSection,
  } = useEditVendorDraft(id!);

  const [showSaveModal, setShowSaveModal] = useState(false);

  const handleSaveClick = async () => {
    const isValidVendorInfo = await vendorInfoForm.trigger();
    if (!isValidVendorInfo) {
      openSection("vendorInformation");
      toast.error(t("toast-complete-required-fields") || "Please complete all required fields");
      return;
    }

    if (branchItems.length === 0) {
      openSection("vendorBranch");
      toast.error(t("toast-branch-required") || "At least one branch is required");
      return;
    }

    if (addressItems.length === 0) {
      openSection("vendorAddress");
      toast.error(t("toast-address-required") || "At least one address is required");
      return;
    }

    const hasAddressForEveryBranch = branchItems.every((branch) =>
      addressItems.some((address) => address.branch === branch.branchName),
    );

    if (!hasAddressForEveryBranch) {
      openSection("vendorAddress");
      toast.error(t("toast-all-branches-must-have-address") || "All branches must have at least one address");
      return;
    }

    if (!hasBillingAddress) {
      openSection("vendorAddress");
      toast.error(t("toast-billing-required") || "One billing address is required");
      return;
    }

    setShowSaveModal(true);
  };

  const handleConfirmSave = useCallback(async () => {
    setShowSaveModal(false);
    await handleSave();
  }, [handleSave]);

  if (isFetchingVendor) {
    return <div>Loading...</div>;
  }

  return (
    <div className="flex flex-col gap-4 pb-20">
      <VendorInformationAccordion
        isOpen={openSections.vendorInformation}
        onToggle={() => toggleSection("vendorInformation")}
        onNext={handleNextFromInformation}
        form={vendorInfoForm}
        t={t}
        isEdit={false}
      />

      <VendorBranchAccordion
        isOpen={openSections.vendorBranch}
        onToggle={() => toggleSection("vendorBranch")}
        onNext={handleNextFromBranch}
        onAddToList={handleAddBranch}
        onEdit={handleEditBranch}
        onDelete={handleDeleteBranch}
        branchItems={branchItems}
        editingBranchIndex={editingBranchIndex}
        form={vendorBranchForm}
        t={t}
      />

      <VendorAddressAccordion
        isOpen={openSections.vendorAddress}
        onToggle={() => toggleSection("vendorAddress")}
        onAddToList={handleAddAddress}
        onEdit={handleEditAddress}
        onDelete={handleDeleteAddress}
        addressItems={addressItems}
        branchOptions={branchOptions}
        hasBillingAddress={hasBillingAddress}
        editingAddressIndex={editingAddressIndex}
        form={vendorAddressForm}
        t={t}
      />

      <div className="sticky bottom-0 bg-white border-t border-border px-6 py-3 flex justify-end gap-2 z-20">
        {showSaveDraft && (
          <Button
            type="button"
            variant="outline"
            color="tertiary"
            onClick={handleSaveDraft}
            disabled={isSavingVendor}
            data-testid="btn-cancel-vendor"
          >
            {t("btn-save-draft") || "Save as Draft"}
          </Button>
        )}
        <Button
          type="button"
          variant="fill"
          color="primary"
          onClick={handleSaveClick}
          disabled={isSavingVendor}
          data-testid="btn-save-vendor"
        >
          {t("btn-save") || "Save"}
        </Button>
      </div>

      <ConfirmationModal
        open={showCancelModal}
        title={t("modal-cancel-title") || "Discard Changes"}
        message={t("modal-cancel-description") || "Are you sure you want to discard your changes?"}
        confirmLabel={t("btn-save-draft") || "Save as Draft"}
        cancelLabel={t("btn-discard") || "Discard"}
        onConfirm={handleSaveDraft}
        onCancel={() => {
          setShowCancelModal(false);
          navigate("/master-data/vendor/draft");
        }}
        icon="danger"
      />

      <ConfirmationModal
        open={showSaveModal}
        title="Save this data?"
        message="Are you sure you want to save this information?"
        cancelLabel={t("btn-cancel") || "Cancel"}
        confirmLabel={t("btn-save") || "Save"}
        onCancel={() => setShowSaveModal(false)}
        onConfirm={handleConfirmSave}
        icon="info"
        loading={isSavingVendor}
      />
    </div>
  );
};

export default EditVendorPage;
